import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

export const NotificationManager = () => {
    const { tournamentId, id } = useParams()
    const activeId = tournamentId || id

    const matchesRef = useRef([])

    useEffect(() => {
        if (!('Notification' in window)) return
        if (Notification.permission === 'default') {
            Notification.requestPermission()
        }
    }, [])

    useEffect(() => {
        if (!activeId) return

        // 1. Fetch initial matches for schedule monitoring
        const fetchMatches = async () => {
            const { data } = await supabase
                .from('matches')
                .select(`
            id, match_time, field, 
            home_team:teams!home_team_id(name), 
            away_team:teams!away_team_id(name)
        `)
                .eq('tournament_id', activeId)
                // Only future matches
                .gte('match_date', new Date().toISOString().split('T')[0])

            if (data) matchesRef.current = data
        }
        fetchMatches()

        // 2. Realtime Subscription for SCORES
        const channel = supabase
            .channel('matches-notifications')
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'matches',
                filter: `tournament_id=eq.${activeId}`
            }, async (payload) => {
                const newRecord = payload.new
                const oldRecord = payload.old

                // Check for Score Update (only if scores are not null)
                if (
                    (newRecord.score_home !== oldRecord.score_home || newRecord.score_away !== oldRecord.score_away) &&
                    newRecord.score_home !== null &&
                    newRecord.score_away !== null
                ) {
                    // Fetch match details including the tournament sport_type
                    const { data: matchDetails } = await supabase
                        .from('matches')
                        .select(`
                            home_team:teams!home_team_id(name), 
                            away_team:teams!away_team_id(name),
                            tournament:tournaments(sport_type)
                        `)
                        .eq('id', newRecord.id)
                        .single()

                    const homeName = matchDetails?.home_team?.name || 'Thuis'
                    const awayName = matchDetails?.away_team?.name || 'Uit'
                    const sportType = matchDetails?.tournament?.sport_type || 'goals'

                    let title = '⚽ GOAL!'
                    if (sportType === 'sets') {
                        title = '🎾 SCORE UPDATE!'
                    } else if (sportType === 'esports') {
                        title = '🎮 SCORE UPDATE!'
                    }

                    sendNotification(
                        title,
                        `${homeName} ${newRecord.score_home} - ${newRecord.score_away} ${awayName}`
                    )
                }
            })
            .subscribe()

        // 3. Time Interval for Upcoming Matches
        const interval = setInterval(() => {
            checkUpcomingMatches()
        }, 60000) // Check every minute

        return () => {
            supabase.removeChannel(channel)
            clearInterval(interval)
        }
    }, [activeId])

    const checkUpcomingMatches = () => {
        const now = new Date()
        const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0')
        const incomingTime = new Date(now.getTime() + 5 * 60000) // 5 minutes from now
        const incomingTimeString = incomingTime.getHours().toString().padStart(2, '0') + ':' + incomingTime.getMinutes().toString().padStart(2, '0')

        matchesRef.current.forEach(match => {
            if (!match.match_time) return

            // Simple check: Exact match on HH:MM string (5 min before)
            // Using "slice(0,5)" to ignore seconds
            if (match.match_time.slice(0, 5) === incomingTimeString) {
                const home = match.home_team?.name || 'Team 1'
                const away = match.away_team?.name || 'Team 2'
                sendNotification(
                    '⏰ Wedstrijd Begint Bijna!',
                    `${home} vs ${away} start over 5 minuten op veld ${match.field || '?'}`
                )
            }
        })
    }

    const sendNotification = (title, body) => {
        if (Notification.permission === 'granted') {
            try {
                new Notification(title, {
                    body,
                    icon: '/logo-icon.webp',
                    vibrate: [200, 100, 200]
                })
            } catch (e) {
                console.error("Notification failed", e)
            }
        }
    }

    return null
}
